import { useEffect, useMemo, useState, type ComponentType, type ReactNode } from 'react';
import {
  Building2, ClipboardCheck, HardDrive, Megaphone, Users, Bell, Palette, Shield, Network, UserCheck, Upload,
  LayoutDashboard, Film, Bot, Zap,
  type LucideIcon,
} from '../../icons';
// 채택 3단계 — 손으로 짠 검색칸을 정본 SearchInput 으로(돋보기·지우기·Esc 가 딸려 온다).
import { SearchInput } from '../../components/shared';
import { SETTINGS_DUOTONE_ENABLED } from '../../lib/layout-shell';
// 시안 아이콘 — web-design-style 재료함의 그룹웨어 두톤 세트. GROUPS 의 lucide 는 그대로 두고
//   그릴 때만 갈아 끼운다(플래그 OFF · 매핑에 없는 자리는 종전 아이콘으로 떨어진다).
import {
  DuoCompany, DuoHr, DuoBell, DuoWorkspace, DuoReservation, DuoBoard, DuoCalendar,
  type DuotoneIconProps,
} from '../../icons/duotone';
// 트리 머리의 제목은 페이지 h1 이 쓰던 것과 **같은 열쇠**를 쓴다(en/ja 도 따라온다).
import { tSettings } from '../../lib/settings-i18n';
import './console-shared.css';
import './CompanyAdmin.css';

import { CompanyInfoSection } from './CompanyInfoSection';
import { ReservationWorkspace } from './ReservationWorkspace';
import { WorkspaceQuotaWorkspace } from './WorkspaceQuotaWorkspace';
import { BoardAdminSection } from './BoardAdminSection';
// 공지 슬라이드 (2026-09-08 · migration 172 · 시안 화면 J) — 대시보드 위젯이 도는 것을 정한다.
import { NoticeSlideSection } from './NoticeSlideSection';
// 위젯 관리 (2026-09-08 · migration 173 · 시안 화면 I) — 회사가 정하는 기본 배치.
import { WidgetLayoutSection } from './WidgetLayoutSection';
// 사내 방송 (2026-09-08 · migration 174 · 시안 화면 K).
import { BroadcastSection } from './BroadcastSection';
// 2026-08-25 — 회사 전용 캘린더 테마. 표는 그대로 두고 chat-server 가 회사 행만 쓴다.
import { CompanyCalendarThemeSection } from './CompanyCalendarThemeSection';
// 판 H-③ — 회사가 쓸 AI 를 고른다. 고를 수 있는 것은 «운영자가 연 목록»뿐이다.
import { CompanyAiSection } from './CompanyAiSection';
// 설계 §07 — 회사 전체 업무자동화를 «보고 끈다». 만드는 것은 여전히 AI 메뉴의 일이다.
import { CompanyAutomationsSection } from './CompanyAutomationsSection';
import { NotificationSection } from './NotificationSection';
// ★ 2026-09-15 — 메뉴 권한은 새 화면(조직 트리 + 부서별 · 개인별 · 확정 시안 v4)으로 건다.
//   종전 `MenuPermissionSection`(+ `MenuMatrix`)은 파일째 남긴다(되돌릴 자리 · 정리는 따로).
import { MenuPermissionScreen } from './menu-perm/MenuPermissionScreen';
import { OrgChartSection } from './OrgChartSection';
// ★ 2026-09-14 — 사업장 칸은 v3 목록 화면(시안 axi-settings-worksites)으로 건다. 종전 WorksiteSection 은 파일째 남긴다(되돌릴 자리).
import { WorksitesListScreen } from './worksites/WorksitesListScreen';
import { DutySection } from './DutySection';
import { OrgBulkImportSection } from './OrgBulkImportSection';
// 사용자 관리는 **다시 복사하지 않는다** — 근태로 옮길 때 만든 것을 그대로 건다(PR#658).
// 같은 화면을 세 벌 두면 셋이 갈라진다. 붙는 자리만 둘이고 구현은 하나다.
import { UserManageWorkspace } from '../AttendancePage/UserManageWorkspace';

/**
 * 통합설정 › 회사 관리 — 2026-08-25.
 *
 * 사용자 지시로 **관리 콘솔(8444)에서 회사 관리자가 쓰는 화면들을 통합설정에도 복사**했다.
 * 콘솔 쪽은 한 줄도 안 건드렸다 — 옮긴 게 아니라 복사다.
 *
 * 왜 2차 내비인가: 통합설정의 기존 탭은 셋(일반·프로필·알림 연동)인데 여기에 회사 관리 화면을
 * 평평하게 이어 붙이면 탭이 열 개를 넘어 가로로 흘러 못 쓴다. 콘솔이 이미 **그룹 → 탭**으로
 * 나눠 두었으므로 그 정보 구조를 그대로 가져와 한 겹 안에 넣는다(사용자가 이미 아는 배치).
 *
 * ── 왜 이만큼인가 (2026-08-25 실측) ────────────────────────────────────────
 * 콘솔의 회사 탭 15개 중 **여기 온 것은 6개**다. 나머지는 안 오는 것이지 빠뜨린 게 아니다:
 *
 *   · 서비스 관리 → **시스템 관리자의 것**이다(2026-08-25 사용자 지시). 어떤 모듈을 쓰게 할지는
 *     회사가 정하는 게 아니라 **주는 쪽**이 정한다 — 워크스페이스와 같은 결이다(운영자가 무엇을
 *     얼마나 줄지 정하고, 회사는 받은 것을 운영한다). 관리 콘솔의 유지보수 그룹으로 갔다.
 *
 *
 *   · 글꼴 관리 → **관리 콘솔에서만 쓴다**(2026-08-25 사용자 결정). #660 에서 한 번 옮겼다가
 *     되돌렸다 — 옮길 수 없어서가 아니라(chat-server `/api/v1/fonts*` 라 잘 됐다) 자리를
 *     콘솔 하나로 정했기 때문이다. 되살리려면 #660 의 `FontWorkspace.tsx`·`FontWorkspace.css`·
 *     `fontIntake.ts`·`api/admin-fonts.ts` 와 `icons` 의 `Type` 을 함께 되살린다.
 *
 *
 *   · **캘린더 테마는 2026-08-25 부터 여기 있다** — 표를 옮긴 게 아니라 **회사 층만** 열었다.
 *     `admin_calendar_themes` 에 `scope`·`institution_id` 칸이 이미 있어서, 회사 행만
 *     chat-server 가 쓴다(`/api/v1/admin/calendar-themes`). 공용 테마는 여전히 콘솔의 것이라
 *     여기서는 보이기만 한다. 마이그레이션 0.
 *
 *   · 메뉴 관리 · 아이콘 관리 · 회사 기본 테마 · 계정 관리 · 대시보드
 *     → **표의 주인이 admin BFF(8444)** 다(`admin_menu_registry`·`admin_icon_registry`·
 *       `admin_calendar_themes`·`admin_company_theme`·`admin_console_accounts`, 대시보드는
 *       BFF 가 여러 소스를 조합). 8443 엣지는 admin BFF 로 **공개 reader 3개만** 프록시하고
 *       (`/menu-config`·`/calendar-themes-config`·`/company-theme-config`), BFF 는 `admin_at`
 *       이라는 **다른 세션**을 쓴다. 그래서 화면만 옮기면 전부 401 이다.
 *       옮길 것은 화면이 아니라 **표의 주인**이다 — 마이그레이션이 딸린 설계 라운드다.
 *   · 공휴일 관리 → 회사 몫은 **이미 8443 에 있다**(근태 › 회사 일정, `institution_holidays`).
 *     콘솔의 그 탭은 전역 표라 쓰기가 SYSTEM_ADMIN 전용이다(PR#648) — 옮겨 봐야 저장이 403 이다.
 *
 * 권한: 이 패널은 `SettingsPage` 가 관리자에게만 렌더한다. 최종 판정은 서버가 한다
 * (chat-server 의 해당 라우트가 전부 `requiredRoles: ['ADMIN','SYSTEM_ADMIN']`).
 */

type PaneId =
  | 'company' | 'reservation' | 'workspace' | 'board'
  | 'user' | 'notify' | 'caltheme'
  // AI 연결 — 2026-09-10 · 판 H-③. 시스템 관리자가 «연» 목록에서 회사가 고른다(8444 와 같은 이름).
  | 'companyai'
  // 업무자동화 — 2026-09-11 · 설계 §07. 회사 전체를 보고 끈다(만들기는 AI 메뉴에 있다).
  | 'companyauto'
  // 조직 (2026-08-28 · 자리 재정의 판 4) — 회사의 뼈대를 정하는 일.
  | 'menuperm' | 'orgchart'
  // 사업장(지사) — 2026-08-30 · 판 K. 「지사주소가 다르다」(사용자).
  | 'worksite'
  // 담당자 — 2026-08-30 · 판 B1. 인사에서 옮겨 왔다.
  | 'duty'
  // 일괄등록 — 2026-08-30 · 판 B3.
  | 'bulk'
  // 공지 슬라이드 — 2026-09-08 · 시안 화면 J.
  | 'noticeslide'
  // 위젯 관리 — 2026-09-08 · 시안 화면 I.
  | 'widgetlayout'
  // 사내 방송 — 2026-09-08 · 시안 화면 K.
  | 'broadcast';

interface PaneDef { id: PaneId; label: string; icon: LucideIcon; hint: string; Body: ComponentType }
interface GroupDef { key: string; label: string; panes: PaneDef[] }

/** 콘솔의 그룹 구성을 그대로 따른다(회사 / 서비스 / 사용자). */
const GROUPS: GroupDef[] = [
  { key: 'company', label: '회사', panes: [
    { id: 'company', label: '회사정보', icon: Building2, hint: '회사·사업자·연락처·로고', Body: CompanyInfoSection },
  ] },
  /**
   * ★ 순서는 **업무 흐름**을 따른다 (2026-08-25 사용자 지시) — 회사를 세우고 → 사람을 넣고 →
   *   그 사람들이 쓸 것을 갖춘다. 콘솔의 그룹 이름을 그대로 쓰되 순서만 이 흐름으로 바꿨다.
   */
  { key: 'user', label: '사용자', panes: [
    // ★ 힌트도 바꿨다 (판 4) — 이제 여기서 부서·팀은 안 다룬다.
    { id: 'user', label: '사용자 관리', icon: Users, hint: '계정 · 비밀번호 · 역할', Body: UserManageWorkspace },
    { id: 'notify', label: '알림 설정', icon: Bell, hint: '이상 징후 메일 수신', Body: NotificationSection },
  ] },
  /**
   * ★★ **조직** (2026-08-28 · 자리 재정의 판 4) — 회사의 «뼈대» 를 정하는 일을 모은다.
   *    메뉴 권한이 사용자 관리 안에 있으면 「계정을 만드는 일」과 「누가 무엇을 보는지 정하는 일」이
   *    한 화면에 섞인다. 사용자 관리는 **계정만** 다루기로 했으므로 이 일이 제 자리를 갖는다.
   *    (조직도도 여기로 오는 것이 설계지만, 지금은 사용자 관리 안에 있어 그대로 둔다.)
   */
  { key: 'org', label: '조직', panes: [
    // ★ 조직도가 사용자 관리에서 왔다 (2026-08-28) — 회사의 뼈대는 계정과 다른 일이다.
    { id: 'orgchart', label: '조직도', icon: Network, hint: '부서 · 팀 · 팀장', Body: OrgChartSection },
    // ★★ 사업장(지사) — 2026-08-30 · 판 K. 「지사들이 있다. 지사주소가 다르다」(사용자).
    //    부서·팀과 **다른 축**이라 조직도에 층을 더하지 않고 **옆에** 세운다 — 같은 팀이
    //    서울·부산에 나뉠 수 있고, 한 지사에 여러 부서가 있다.
    { id: 'worksite', label: '사업장', icon: Building2, hint: '지사 주소 · 임직원 근무지', Body: WorksitesListScreen },
    { id: 'menuperm', label: '메뉴 권한', icon: Shield, hint: '회사 · 본부 · 팀 · 개인', Body: MenuPermissionScreen },
    // ★★ 담당자 (2026-08-30 · 판 B1) — 인사 트리에서 **주소째** 옮겨 왔다.
    //    「누가 무슨 업무를 맡는가」는 회사의 뼈대라 조직 옆이 맞다.
    { id: 'duty', label: '담당자', icon: UserCheck, hint: '업무를 사람에게 따로', Body: DutySection },
    // ★★ 일괄등록 (2026-08-30 · 판 B3) — 조직은 **없었고**, 사용자 것은 만들어 놓고
    //    **아무 데도 안 붙어 있었다**. 둘을 한 자리에 세운다.
    { id: 'bulk', label: '일괄등록', icon: Upload, hint: '부서 · 팀 · 사용자 CSV', Body: OrgBulkImportSection },
  ] },
  /**
   * ★★ **대시보드** (2026-09-08) — 위젯이 무엇을 보여줄지 정하는 일을 모은다.
   *    위젯을 정하는 사람은 **회사 관리자**라 관리 콘솔(8444)이 아니라 여기가 제 자리다.
   *    뒤의 판에서 「위젯 관리」(기본 배치)·「사내 방송」이 같은 묶음으로 들어온다.
   */
  { key: 'dashboard', label: '대시보드', panes: [
    // ★ 배치가 먼저다 — 「무엇을 보여줄지」보다 「어떤 판을 주는지」가 앞선다.
    { id: 'widgetlayout', label: '위젯 관리', icon: LayoutDashboard, hint: '회사가 정하는 기본 배치', Body: WidgetLayoutSection },
    { id: 'noticeslide', label: '공지 슬라이드', icon: Megaphone, hint: '이미지 · 텍스트가 한 줄로', Body: NoticeSlideSection },
    { id: 'broadcast', label: '사내 방송', icon: Film, hint: '채널 · 영상 · 허용 도메인', Body: BroadcastSection },
  ] },
  { key: 'service', label: '서비스', panes: [
    // 2026-08-25 — 회사가 쓰는 자리는 여기 하나뿐이라 이름에 붙이던 '(회사용)' 을 뺐다(사용자 지시).
    //   회사에 **얼마를 줄지**는 관리 콘솔의 워크스페이스 관리(시스템용)가 정한다 —
    //   거기는 회사 전체 용량 하나만 다루고, 운영자만 본다. 여기서는 그 받은 용량을 나눈다.
    { id: 'workspace', label: '워크스페이스 관리', icon: HardDrive, hint: '받은 용량을 부서·팀·개인에게', Body: WorkspaceQuotaWorkspace },
    { id: 'reservation', label: '예약관리', icon: ClipboardCheck, hint: '회의실·자원·담당자', Body: ReservationWorkspace },
    { id: 'board', label: '게시판 관리', icon: Megaphone, hint: '게시판·알림 정책', Body: BoardAdminSection },
    { id: 'caltheme', label: '캘린더 테마', icon: Palette, hint: '우리 회사 전용 색', Body: CompanyCalendarThemeSection },
  ] },
  /**
   * ★★ **AI** (2026-09-11) — AI 에 관한 회사의 결정을 한 자리에 모은다.
   *
   *    「AI 연결」은 2026-09-10 에 **서비스** 묶음에 들어갔었다. 거기에 「업무자동화」가 더해지면
   *    AI 를 정하는 일이 예약·게시판·용량 사이에 흩어진다 — 관리자가 「AI 설정이 어디 있더라」를
   *    매번 다시 찾게 된다. 그래서 묶음을 새로 내고 **AI 연결을 함께 옮겼다**(화면·주소·컴포넌트는
   *    그대로고 «어느 이름표 밑에 서는가»만 바뀐다).
   *
   *    ★ 순서 — **고르는 것이 먼저다.** 어떤 모델을 쓸지 정하지 않으면 자동화가 무엇으로 도는지도
   *      정해지지 않는다.
   */
  { key: 'ai', label: 'AI', panes: [
    { id: 'companyai', label: 'AI 연결', icon: Bot, hint: 'AI 메뉴·챗에서 쓸 모델', Body: CompanyAiSection },
    // 만드는 자리는 여전히 AI 메뉴다 — 여기서는 «보고 끄는» 것만 한다(설계 §07).
    { id: 'companyauto', label: '업무자동화', icon: Zap, hint: '회사 전체 · 멈추기', Body: CompanyAutomationsSection },
  ] },
];

/**
 * 자리별 두톤 아이콘 (2026-08-26). 여섯은 재료함에서 그대로 왔고, «회사» 하나만 없어서
 * 그 규격대로 그렸다 — 재료함에 담으면 참조로 바꾼다.
 * 매핑에 없는 자리는 `GROUPS` 의 lucide 로 떨어지므로, 칸이 늘어도 아이콘이 비지 않는다.
 */
const DUOTONE_BY_PANE: Partial<Record<PaneId, ComponentType<DuotoneIconProps>>> = {
  company: DuoCompany,
  user: DuoHr,
  notify: DuoBell,
  workspace: DuoWorkspace,
  reservation: DuoReservation,
  board: DuoBoard,
  caltheme: DuoCalendar,
};

const PANES: PaneDef[] = GROUPS.flatMap((g) => g.panes);

const isPaneId = (v: string | null | undefined): v is PaneId => !!v && PANES.some((p) => p.id === v);

/**
 * ★ 2026-09-14 (사업장 v3) — 칸을 **주소로** 열 수 있게 받는 것 셋을 더했다(모두 선택 · 없으면 종전 그대로):
 *   · `pane` — 열 칸(`/settings?pane=worksite`). 값이 바뀌면 그 칸으로 간다(상세 → 「목록으로」 · 「회사정보로 이동」).
 *   · `onPaneSelect` — 메뉴를 누르면 알린다(주소를 따라 바꾸는 쪽이 쓴다).
 *   · `body` — 오른쪽 본문을 갈아 끼운다(사업장 상세 라우트가 메뉴는 그대로 두고 본문만 상세로).
 */
export interface CompanyAdminPanelProps {
  pane?: string | null;
  onPaneSelect?: (id: PaneId) => void;
  body?: ReactNode;
}

export function CompanyAdminPanel({ pane: paneProp, onPaneSelect, body }: CompanyAdminPanelProps = {}): JSX.Element {
  const [pane, setPane] = useState<PaneId>(isPaneId(paneProp) ? paneProp : 'company');
  useEffect(() => { if (isPaneId(paneProp)) setPane(paneProp); }, [paneProp]);
  const [q, setQ] = useState('');
  const active = PANES.find((p) => p.id === pane) ?? PANES[0]!;
  const Body = active.Body;

  /**
   * 설정 메뉴 검색 (2026-08-26 시안의 트리 머리) — 항목이 일곱뿐이라 서버에 물을 것이 없다.
   * 이름과 부제를 함께 본다("용량" 으로 워크스페이스 관리가 걸리게).
   *
   * ★ **거르는 것은 목록뿐이고 오른쪽 화면은 그대로 둔다** — 검색어를 치는 도중에 보던
   *   화면이 발밑에서 바뀌면 안 된다. 고른 항목이 걸러져 나가면 켜진 표시만 잠시 안 보인다.
   */
  const needle = q.trim().toLowerCase();
  const groups = useMemo(() => {
    if (!needle) return GROUPS;
    return GROUPS
      .map((g) => ({ ...g, panes: g.panes.filter((p) => `${p.label} ${p.hint}`.toLowerCase().includes(needle)) }))
      .filter((g) => g.panes.length > 0);
  }, [needle]);

  return (
    <div className="cadm">
      <nav className="cadm__nav" aria-label="회사 관리">
        {/* ── 트리 머리 — 제목과 검색 (2026-08-26) ─────────────────────────────
            시안의 접기 단추와 «쓰는 자리» 발은 넣지 않았다(사용자 지시 — 제목과 검색만).
            머리를 `<nav>` **안**에 두는 것은 일부러다: 바깥으로 빼면 격자 칸이 하나 늘어
            «셸의 두 번째 칸» 을 만든 규칙(위 블록)을 통째로 다시 써야 한다. */}
        <div className="cadm__head">
          <h2 className="cadm__title">{tSettings('settings.company.heading')}</h2>
          <SearchInput
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onClear={() => setQ('')}
            placeholder="설정 메뉴 검색"
            aria-label="설정 메뉴 검색"
            fullWidth
          />
        </div>

        {groups.map((g) => (
          <div className="cadm__group" key={g.key}>
            <div className="cadm__glabel">{g.label}</div>
            {g.panes.map((p) => {
              // 두톤 세트는 28px 로 그려져 있어 16px 아래로 줄이면 두 톤이 뭉갠다 → 15 → 17px.
              const Duo = SETTINGS_DUOTONE_ENABLED ? DUOTONE_BY_PANE[p.id] : undefined;
              const Icon = Duo ?? p.icon;
              const on = p.id === pane;
              return (
                <button
                  key={p.id}
                  type="button"
                  className={`cadm__item${on ? ' is-on' : ''}`}
                  aria-current={on ? 'page' : undefined}
                  onClick={() => { setPane(p.id); onPaneSelect?.(p.id); }}
                >
                  <Icon size={Duo ? 17 : 15} aria-hidden />
                  <span className="cadm__nm">{p.label}</span>
                  <span className="cadm__hint">{p.hint}</span>
                </button>
              );
            })}
          </div>
        ))}

        {groups.length === 0 && (
          <p className="cadm__none">‘{q.trim()}’ 에 맞는 설정이 없습니다</p>
        )}
      </nav>

      <section className="cadm__body" aria-label={active.label}>
        {body ?? <Body />}
      </section>
    </div>
  );
}
